
import { Card } from "@/components/ui/card";
import { AnalyticsMetric } from "@/types/assessment/analytics";
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  HelpCircle
} from "lucide-react";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface MetricCardProps {
  metric: AnalyticsMetric;
  comparisonDate?: string;
}

export const MetricCard = ({ metric, comparisonDate }: MetricCardProps) => {
  const getTrendIcon = () => {
    switch (metric.trend) {
      case "up":
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case "down":
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-gray-500" />;
    }
  };

  const getMetricExplanation = () => {
    switch (metric.name) {
      case "Market Position":
        return "Calculated from strategy and innovation scores, indicating your competitive standing in the market.";
      case "AI Readiness":
        return "Calculated from data, talent, and deployment scores, reflecting your organization's preparedness for AI adoption.";
      case "Industry Rank":
        return "Your position relative to other organizations in your industry based on overall score (lower is better).";
      case "Innovation Potential":
        return "Based on your innovation dimension score, indicating capability to implement new AI solutions.";
      case "Data Maturity":
        return "Based on your data dimension score, indicating the quality and usability of your data assets for AI.";
      case "Overall Maturity":
        return "Your aggregated score across all dimensions, representing overall AI maturity level.";
      default:
        return "A key metric derived from your assessment scores.";
    }
  };

  return (
    <Card className="p-4">
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-1">
            <h3 className="text-sm font-medium text-gray-500">{metric.name}</h3>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button 
                    className="inline-flex items-center justify-center cursor-help"
                    aria-label={`${metric.name} explanation`}
                  >
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent 
                  className="max-w-xs p-3 bg-white shadow-lg rounded-md border text-sm"
                  sideOffset={5}
                >
                  <p className="text-sm">{getMetricExplanation()}</p>
                  
                  {comparisonDate && metric.change !== undefined && (
                    <p className="text-xs mt-2 text-muted-foreground">
                      Change calculated from your previous assessment on {comparisonDate}.
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex items-baseline space-x-2 mt-1">
            <p className="text-2xl font-bold">
              {typeof metric.value === 'number' && metric.value % 1 === 0
                ? metric.value
                : metric.value.toFixed(1)}
            </p>
            {metric.change !== undefined && (
              <div className={`flex items-center text-sm ${metric.change > 0 ? "text-green-500" : metric.change < 0 ? "text-red-500" : "text-gray-500"}`}>
                {metric.change > 0 ? (
                  <span className="flex items-center">
                    <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                    {Math.abs(metric.change)}%
                  </span>
                ) : metric.change < 0 ? (
                  <span className="flex items-center">
                    <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    {Math.abs(metric.change)}%
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Minus className="h-3 w-3 mr-1" />
                    No change
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="p-2 rounded-full bg-gray-100">
          {getTrendIcon()}
        </div>
      </div>
    </Card>
  );
};
